import type { ApiClient } from '@carbonwebmail/api-client'
import { Link, Outlet, useLocation, useSearchParams } from 'react-router'
import { NEW_VIEW } from './useContacts.js'
import { useContactViews } from './useContactViews.js'

/**
 * Everything about an audience, on one screen.
 *
 * The list, the saved views, the duplicates, the fields somebody declared and
 * the import were five entries in the sidebar and five screens. They are one
 * subject — who we have and what we know about them — and splitting them put
 * the thing you reach for next behind a navigation step: importing contacts
 * meant leaving the list, and coming back to it meant finding it again.
 *
 * **The tabs are views, and a view is not a segment.** A view says what
 * somebody is looking at: which rows, in what order, with which columns
 * showing. A segment says who a campaign reaches, and has members as well as
 * rules. They were the same thing here, so adding a tab created an audience —
 * segments are made on the Segments screen, and this makes none.
 *
 * An organisation is created with three views — All, Subscribed, Unsubscribed
 * — and each is deletable like any other, so a board can end up with no tabs at
 * all. That is still the board: `/contacts` with no view shows everyone, which
 * is where the heading and the sidebar lead.
 *
 * **Every tab is a view now.** Fields was one, and it is gone: a column is
 * renamed, retyped and deleted from its own heading, which is where somebody
 * looks for it, and the `+` past the last column declares one — reachable even
 * on an empty board, because the table is drawn whether or not it has rows. A
 * screen listing the columns was a second place to manage them.
 *
 * Each panel keeps its own view model. This screen holds the tabs, and **owns
 * the list of views behind them**: a screen that draws something owns the state
 * behind it, and anything that changes it asks the owner through the outlet.
 *
 * **Saving a view happens in `ContactsList`**, because what a view *is* — the
 * filter, the order, the hidden columns — is the state that screen holds. The
 * `+` at the end of the strip therefore asks rather than does: it adds
 * `new-view` to the url, and the list, which has all three, opens the dialog.
 * The url is the one channel these two screens already share, and the
 * alternative was lifting the board's whole arrangement up here to be passed
 * back down. It is offered only while the list is what is on screen, because
 * what it saves is what is on screen.
 *
 * **The chosen view is in the url**, not in a component, so a refresh, a back
 * button and a pasted link all land on the same rows.
 */
export function ContactsBoard({ client }: { readonly client: ApiClient }) {
  /**
   * **One list of views, owned here and handed down.**
   *
   * Both this screen and `ContactsList` used to call `useContactViews` for
   * themselves — two instances, two lists, two loads. Saving a view reloaded
   * the list's copy while the tab strip, which is drawn from this one, was
   * never asked again: the view really was created and no tab appeared, which
   * from the outside is indistinguishable from it not being created at all.
   *
   * So the board owns it and passes the commands through the outlet. A screen
   * that shows something owns the state behind it; anything that changes it
   * asks the owner.
   */
  const views = useContactViews({ client })
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const view = params.get('view') ?? undefined
  const listing = pathname === '/contacts' || pathname === '/contacts/'

  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Contacts</h1>
        {/*
          Only the import is up here. The controls that *change* rows — New
          contact and Duplicates — sit with the rows in `ContactsList`, because
          that is where the list that has to reload afterwards lives.
        */}
        <Link
          className="rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-t2 hover:text-t1"
          to="/contacts/import"
        >
          Import from CSV
        </Link>
      </header>

      <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-1 border-line border-b">
        {views.views.map((saved) => (
          <Tab
            chosen={listing && view === saved.viewId}
            key={saved.viewId}
            to={`/contacts?view=${encodeURIComponent(saved.viewId)}`}
          >
            {saved.name}
          </Tab>
        ))}
        {listing ? (
          <Link
            aria-label="Save this arrangement as a view"
            className="-mb-px border-transparent border-b-2 py-2.5 font-semibold text-[13px] text-t3 hover:text-t1"
            to={savingView(view)}
          >
            +
          </Link>
        ) : null}
      </div>

      <Outlet context={views} />
    </>
  )
}

/**
 * The same board, asking the list to open the save dialog.
 *
 * The open view is kept, because what is being saved is an arrangement of it —
 * and dropping it would move the rows out from under somebody mid-press.
 */
function savingView(view: string | undefined): string {
  const params = new URLSearchParams(view === undefined ? [] : [['view', view]])
  params.set(NEW_VIEW, '')
  return `/contacts?${params.toString()}`
}

function Tab({
  chosen,
  to,
  children,
}: {
  readonly chosen: boolean
  readonly to: string
  readonly children: React.ReactNode
}) {
  return (
    <Link
      aria-current={chosen ? 'page' : undefined}
      className={`-mb-px border-b-2 py-2.5 font-semibold text-[13px] ${
        chosen ? 'border-brand text-brand' : 'border-transparent text-t2 hover:text-t1'
      }`}
      to={to}
    >
      {children}
    </Link>
  )
}
